import React from 'react';
import {
  BodyText,
  FlexColumn,
  FlexRow,
  InfoIcon,
  TableData,
} from '@make-software/cspr-design';
import { Trans, useTranslation } from 'react-i18next';
import { Table, Tooltip, Link } from '@block-explorer/ui';
import SectionTable from '../../../components/section-table/section-table';
import {
  DetailsTableRow,
  InfoSvgIcon,
  TileSide,
  TileSideHeader,
} from '../../../components';
import RandomValidatorRow from './RandomValidatorRow';
import { useGetRandomReshuffledValidators } from '../hooks/use-get-random-reshuffled-validators';
import styled from 'styled-components';
import { SectionPaginationType } from '../../../components/section-table/section-table-pagination';
import { VALIDATOR_AUCTION_PATH } from '../../../router/paths';
import { useCustomHistory } from '../../../router/navigation';
import Skeleton from 'react-loading-skeleton';

const TableContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    padding: '0 20px',
  })
);

const RandomValidatorsTile = () => {
  const { t } = useTranslation();
  const history = useCustomHistory();
  const {
    data: randomReshuffledValidators,
    loading,
    error,
    paginationProps,
  } = useGetRandomReshuffledValidators();

  if (loading) {
    return (
      <TileSide>
        <TileSideHeader title={<Trans t={t}>Stake on Casper</Trans>} />
        <Table
          renderData={() => (
            <DetailsTableRow>
              <TableData>
                <FlexColumn gap={16}>
                  <Skeleton count={1} height={'88px'} width={'100%'} />
                  <Skeleton count={1} height={'28px'} width={'100%'} />
                  <Skeleton count={1} height={'70px'} width={'100%'} />
                </FlexColumn>
              </TableData>
            </DetailsTableRow>
          )}
        />
      </TileSide>
    );
  }

  if (randomReshuffledValidators?.length === 0) {
    return (
      <TileSide>
        <TileSideHeader
          title={<Trans t={t}>Stake on Casper</Trans>}
          justify={'space-between'}
          icon={
            <Tooltip
              tooltipContent={
                <>
                  <Trans t={t}>
                    We randomly display validators that are both verified and
                    active to spotlight smaller operators and increase
                    decentralization.
                  </Trans>
                  <br />
                  <Trans t={t}>
                    Full details to make an informed choice are on each
                    Validator’s Details page and on the Validators page.
                  </Trans>
                </>
              }
            >
              <InfoSvgIcon src={InfoIcon} style={{ marginLeft: 6 }} size={20} />
            </Tooltip>
          }
        />

        <Table
          renderData={() => (
            <DetailsTableRow>
              <TableData>
                <FlexColumn itemsSpacing={8}>
                  <BodyText size={3} scale="xs" wordBreak>
                    <Trans t={t}>
                      Stake on Casper allows users to lock their tokens in the
                      network’s staking contract to support consensus, secure
                      the blockchain, and earn rewards.
                    </Trans>
                  </BodyText>
                  <BodyText size={3} scale="xs" wordBreak>
                    <Trans t={t}>
                      By staking, you contribute to the stability and
                      decentralization of the Casper ecosystem while receiving
                      periodic payouts based on your staked amount and your
                      validator’s performance.
                    </Trans>
                  </BodyText>
                </FlexColumn>
                <FlexRow style={{ padding: '16px 0' }}>
                  <Link
                    color={'primaryBlue'}
                    onClick={() => history.push(VALIDATOR_AUCTION_PATH)}
                  >
                    <BodyText size={3} variation="blue" noWrap>
                      <Trans t={t}>View all Validators</Trans>
                    </BodyText>
                  </Link>
                </FlexRow>
              </TableData>
            </DetailsTableRow>
          )}
        />
      </TileSide>
    );
  }

  return (
    <TileSide>
      <TileSideHeader
        title={<Trans t={t}>Stake on Casper</Trans>}
        justify={'space-between'}
        icon={
          <Tooltip
            tooltipContent={
              <>
                <Trans t={t}>
                  We randomly display validators that are both verified and
                  active to spotlight smaller operators and increase
                  decentralization.
                </Trans>
                <br />
                <Trans t={t}>
                  Full details to make an informed choice are on each
                  Validator’s Details page and on the Validators page.
                </Trans>
              </>
            }
          >
            <InfoSvgIcon src={InfoIcon} style={{ marginLeft: 6 }} size={20} />
          </Tooltip>
        }
      />
      <TableContainer>
        <SectionTable
          data={randomReshuffledValidators}
          loading={loading}
          error={error}
          paginationProps={paginationProps}
          renderPaginatedData={(validators) =>
            validators.map((validator) => (
              <RandomValidatorRow key={validator.key} validator={validator} />
            ))
          }
          paginationType={SectionPaginationType.extendedPagination}
          footerLink={
            <Link
              color={'primaryBlue'}
              onClick={() => history.push(VALIDATOR_AUCTION_PATH)}
            >
              <BodyText size={3} variation="blue" noWrap>
                <Trans t={t}>View all Validators</Trans>
              </BodyText>
            </Link>
          }
        />
      </TableContainer>
    </TileSide>
  );
};

export default RandomValidatorsTile;
